import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTtlockLocks, updateTtlockLockSettings, TtlockLock } from '../api.ttlock';

const getToken = () => localStorage.getItem('token') || '';

const ALL_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

const LockDetail: React.FC = () => {
  const { lockId } = useParams<{ lockId: string }>();
  const navigate = useNavigate();

  const [lock, setLock] = useState<TtlockLock | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pinLength, setPinLength] = useState(6);
  const [selectedDigits, setSelectedDigits] = useState<Set<string>>(new Set(ALL_DIGITS));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const locks = await getTtlockLocks(getToken());
        const found = locks.find(l => l.lockId === Number(lockId));
        if (!found) {
          setError('Lock not found.');
        } else {
          setLock(found);
          setPinLength(found.pinLength ?? 6);
          setSelectedDigits(new Set((found.allowedDigits ?? '0123456789').split('')));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load lock');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lockId]);

  const toggleDigit = (d: string) => {
    setSelectedDigits(prev => {
      if (prev.has(d) && prev.size <= 1) return prev;
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const allowedDigits = ALL_DIGITS.filter(d => selectedDigits.has(d)).join('');
      const updated = await updateTtlockLockSettings(getToken(), Number(lockId), { pinLength, allowedDigits });
      setLock(updated);
      setSuccess('Lock settings saved.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ maxWidth: 600, margin: '30px auto', padding: '0 16px' }}>Loading lock...</div>;
  }

  if (!lock) {
    return <div style={{ maxWidth: 600, margin: '30px auto', padding: '0 16px', color: '#dc2626' }}>{error || 'Lock not found.'}</div>;
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px' }}>
      <button
        onClick={() => navigate(-1)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 20, fontSize: '0.9rem' }}
      >
        ← Back
      </button>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 28 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
          {lock.lockAlias || lock.lockName}
        </h2>
        {lock.lockAlias && (
          <div style={{ color: '#718096', fontSize: '0.85rem', marginBottom: 4 }}>{lock.lockName}</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.82rem', color: '#718096', marginBottom: 24 }}>
          <span>TTLock ID: {lock.lockId}</span>
          {lock.electricQuantity != null && <span>🔋 {lock.electricQuantity}%</span>}
          {lock.lastSyncedAt && <span>Synced: {new Date(lock.lastSyncedAt).toLocaleString()}</span>}
          <span style={{ padding: '2px 8px', borderRadius: 12, fontWeight: 600, background: lock.isActive ? '#dcfce7' : '#fee2e2', color: lock.isActive ? '#166534' : '#991b1b' }}>
            {lock.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {success && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#166534', fontWeight: 500 }}>
            {success}
          </div>
        )}
        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontWeight: 500 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSave}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>PIN Length</label>
            <input
              type="number"
              min={4}
              max={9}
              value={pinLength}
              onChange={e => setPinLength(parseInt(e.target.value) || 6)}
              className="form-control"
              style={{ width: 100 }}
            />
            <div style={{ fontSize: '0.78rem', color: '#718096', marginTop: 4 }}>
              Number of digits in the generated PIN (4–9).
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Allowed Digits</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ALL_DIGITS.map(d => {
                const active = selectedDigits.has(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDigit(d)}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      border: `2px solid ${active ? '#3b82f6' : '#e2e8f0'}`,
                      background: active ? '#dbeafe' : '#f8fafc',
                      color: active ? '#1d4ed8' : '#9ca3af',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#718096', marginTop: 6 }}>
              Select which digits the lock keypad supports. Active: <code>{ALL_DIGITS.filter(d => selectedDigits.has(d)).join('')}</code>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: 20 }}>
            <button type="submit" className="app-btn app-btn-green app-btn-standard" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="app-btn app-btn-standard"
              onClick={() => navigate(-1)}
              disabled={saving}
              style={{ background: '#e2e8f0', color: '#475569' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LockDetail;
